"use client";

import { createContext, useContext } from "react";
import type { BrandDetail } from "@/hooks/use-brands";

interface BrandContextValue {
  brand: BrandDetail;
  currentUserRole: string;
  // TODO(ai-cost-tracking): wire real org-owner flag from /brands/:id or session.
  // For now this is hardcoded false at the provider level so permission checks
  // that depend on org ownership (e.g. canOverrideBudget) are safely gated.
  isOrgOwner?: boolean;
  refetchBrand?: () => void;
}

const BrandContext = createContext<BrandContextValue | null>(null);

export function BrandProvider({
  brand,
  currentUserRole,
  isOrgOwner = false,
  refetchBrand,
  children,
}: BrandContextValue & { children: React.ReactNode }) {
  return (
    <BrandContext.Provider value={{ brand, currentUserRole, isOrgOwner, refetchBrand }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrandContext(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) {
    throw new Error("useBrandContext must be used inside BrandProvider");
  }
  return ctx;
}

/**
 * Non-throwing variant for components mounted outside a BrandProvider
 * (e.g. the Sidebar, which renders on non-brand-scoped routes too).
 */
export function useBrandContextOptional(): BrandContextValue | null {
  return useContext(BrandContext);
}
